/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  *
  * Pure view logic for the Claude usage panel. Kept out of the component so the
  * aggregation is testable without rendering -- the interesting bugs here are
  * arithmetic, not JSX.
  */

/**
 * One weekly rollup row, as the backend serialises it.
 *
 * A SECOND declaration of the backend's WeeklyRow, and deliberately so. The app package
 * cannot import from packages/backend, and the only cross-package home in this repo is a
 * permissions plugin -- a poor place for a metrics wire type. Sharing the declaration
 * there would buy a compile-time link but would NOT catch the failure that actually
 * reaches users: a cached SPA talking to a freshly deployed backend, where the two halves
 * were compiled at different times. So the boundary is CHECKED at runtime instead, by
 * parseWeeklyRows below, which covers both drift and version skew. See the PR discussion
 * on this file.
 */
export type WeeklyRow = {
  isoYear: number;
  isoWeek: number;
  org: string;
  repo: string;
  model: string;
  /** PR author, or `_none_` when the run had none. Never a wildcard: the route returns the
   *  full grain and the client aggregates, so nothing is pre-collapsed. */
  author: string;
  /** github.actor for the run; same sentinel as author. */
  trigger: string;
  msgs: number;
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  runCount: number;
  /** PR grain -- present on every view's rows now that all periods are read-time
   *  rollups of raw runs; '_none_' = no PR. Optional for one-deploy-behind skew. */
  pr?: string;
  /** The GitHub job key that produced the runs (claude-review, claude-security-review,
   *  ...) -- the run-TYPE dimension. Same read-time provenance and deploy-skew
   *  optionality as pr, but no sentinel: the consumer rejects messages without one. */
  job?: string;
  /**
   * Owning team, enriched CLIENT-SIDE from the catalog before grouping (never on
   * the wire). 'not in catalog' / 'lookup failed' are honest buckets, not teams.
   */
  team?: string;
};

/** Numeric fields every row must carry; any missing one makes the totals meaningless. */
const WEEKLY_NUMERIC_FIELDS = [
  'isoYear',
  'isoWeek',
  'msgs',
  'inputTokens',
  'outputTokens',
  'cacheWriteTokens',
  'cacheReadTokens',
  'runCount',
] as const;

const WEEKLY_STRING_FIELDS = ['org', 'repo', 'model', 'author', 'trigger'] as const;

/**
 * Validates the wire rows rather than asserting them with `as`.
 *
 * Without this, a renamed or dropped backend field reads as `undefined`, `totals()` does
 * `+= undefined` -> NaN, and formatTokens maps non-finite to an em dash -- so the Total
 * column silently blanks for every row while the table, the week selector and a green
 * Reporting-status card all keep working. That is this feature's core failure mode
 * (under-reporting that looks fine) arriving through the serialisation boundary.
 *
 * Returns the valid rows and a count of rejects, so the caller can refuse to render a
 * partial spend report rather than quietly showing a smaller number.
 */
export function parseWeeklyRows(input: unknown): {
  rows: WeeklyRow[];
  invalid: number;
} {
  if (!Array.isArray(input)) return { rows: [], invalid: 0 };
  const rows: WeeklyRow[] = [];
  let invalid = 0;
  for (const candidate of input) {
    if (!candidate || typeof candidate !== 'object') {
      invalid += 1;
      continue;
    }
    const r = candidate as Record<string, unknown>;
    const numbersOk = WEEKLY_NUMERIC_FIELDS.every(
      f => typeof r[f] === 'number' && Number.isFinite(r[f] as number),
    );
    const stringsOk = WEEKLY_STRING_FIELDS.every(f => typeof r[f] === 'string');
    const prOk = r.pr === undefined || typeof r.pr === 'string';
    const jobOk = r.job === undefined || typeof r.job === 'string';
    if (!numbersOk || !stringsOk || !prOk || !jobOk) {
      invalid += 1;
      continue;
    }
    rows.push(candidate as WeeklyRow);
  }
  return { rows, invalid };
}

/** Mirrors the backend sentinel for a run with no PR author / no resolvable trigger. */
export const UNKNOWN_ACTOR = '_none_';

/** The whole /weekly envelope, validated rather than asserted. */
export type ActorFilter = { field: 'author' | 'trigger'; login: string };

/**
 * Run-grain reported cost, as the server aggregates it (see the backend's
 * rollupCosts): no model dimension exists at the source, so this arrives as a
 * SEPARATE list from `rows` and is only summable at groupings that never split a
 * run. costedRuns < totalRuns means partial coverage (pre-field emitters).
 */
export type RunCost = {
  org: string;
  repo: string;
  author: string;
  trigger: string;
  /** Qualified `org/repo#n` or '_none_'; present on every payload now (raw-backed
   *  reads at all granularities). Optional for one-deploy-behind skew. */
  pr?: string;
  /** Run type (GitHub job key); same contract as WeeklyRow.job. */
  job?: string;
  costUsd: number;
  costedRuns: number;
  totalRuns: number;
};

/**
 * Tolerant on ABSENCE (an older backend has no runCosts field -- reported cost
 * simply does not render), strict on SHAPE (a malformed entry poisons sums the
 * same way a malformed row would, so the whole list is rejected to []).
 */
export function parseRunCosts(input: unknown): RunCost[] {
  if (input === undefined) return [];
  if (!Array.isArray(input)) return [];
  const out: RunCost[] = [];
  for (const entry of input) {
    if (!entry || typeof entry !== 'object') return [];
    const r = entry as Record<string, unknown>;
    const strings = ['org', 'repo', 'author', 'trigger'] as const;
    const numbers = ['costUsd', 'costedRuns', 'totalRuns'] as const;
    if (!strings.every(f => typeof r[f] === 'string')) return [];
    if (r.pr !== undefined && typeof r.pr !== 'string') return [];
    if (r.job !== undefined && typeof r.job !== 'string') return [];
    if (!numbers.every(f => typeof r[f] === 'number' && Number.isFinite(r[f] as number))) {
      return [];
    }
    out.push({
      org: r.org as string,
      repo: r.repo as string,
      author: r.author as string,
      trigger: r.trigger as string,
      ...(r.pr !== undefined ? { pr: r.pr as string } : {}),
      ...(r.job !== undefined ? { job: r.job as string } : {}),
      costUsd: r.costUsd as number,
      costedRuns: r.costedRuns as number,
      totalRuns: r.totalRuns as number,
    });
  }
  return out;
}

/**
 * Run-grain reported savings, one cell per (org, repo, author, trigger[, pr][, job])
 * with the per-METHOD breakdown nested inside -- method never widens the grain
 * because a run can report several methods, and method cells would overlap on
 * runs (see the backend's rollupSavings). Cells exist only where savings were
 * reported; savedRuns < totalRuns is partial coverage, like costedRuns.
 */
export type RunSaving = {
  org: string;
  repo: string;
  author: string;
  trigger: string;
  /** Same qualified `org/repo#n` / '_none_' contract as RunCost.pr. */
  pr?: string;
  /** Run type (GitHub job key); same contract as RunCost.job. */
  job?: string;
  savingsUsd: number;
  savedRuns: number;
  /**
   * Distinct runs whose savings include at least one COUNTED method — the
   * intervention-attributable population, computed server-side because method
   * run counts overlap and cannot be summed into a distinct-run figure here.
   * Optional for one-deploy-behind skew: absent withholds the attributable
   * "N of M runs" claim rather than approximating it.
   */
  attrSavedRuns?: number;
  totalRuns: number;
  /** uses/countedRuns: optional use counting — see the backend's WeeklySavingCell. */
  methods: {
    method: string;
    usd: number;
    runs: number;
    uses?: number;
    countedRuns?: number;
  }[];
};

/**
 * Same posture as parseRunCosts: tolerant on ABSENCE (an older backend has no
 * runSavings -- savings simply do not render), strict on SHAPE (one malformed
 * entry rejects the whole list to []).
 */
export function parseRunSavings(input: unknown): RunSaving[] {
  if (input === undefined) return [];
  if (!Array.isArray(input)) return [];
  const out: RunSaving[] = [];
  for (const entry of input) {
    if (!entry || typeof entry !== 'object') return [];
    const r = entry as Record<string, unknown>;
    const strings = ['org', 'repo', 'author', 'trigger'] as const;
    const numbers = ['savingsUsd', 'savedRuns', 'totalRuns'] as const;
    if (!strings.every(f => typeof r[f] === 'string')) return [];
    if (r.pr !== undefined && typeof r.pr !== 'string') return [];
    if (r.job !== undefined && typeof r.job !== 'string') return [];
    if (!numbers.every(f => typeof r[f] === 'number' && Number.isFinite(r[f] as number))) {
      return [];
    }
    if (
      r.attrSavedRuns !== undefined &&
      (typeof r.attrSavedRuns !== 'number' || !Number.isFinite(r.attrSavedRuns))
    ) {
      return [];
    }
    if (!Array.isArray(r.methods)) return [];
    const methods: RunSaving['methods'] = [];
    for (const m of r.methods) {
      if (!m || typeof m !== 'object') return [];
      const e = m as Record<string, unknown>;
      if (typeof e.method !== 'string' || !e.method) return [];
      if (typeof e.usd !== 'number' || !Number.isFinite(e.usd)) return [];
      if (typeof e.runs !== 'number' || !Number.isFinite(e.runs)) return [];
      // Optional pair, validated together: a use count without its coverage
      // denominator (or vice versa) is malformed, and shape errors reject the
      // whole list like every other field here.
      const hasUses = e.uses !== undefined || e.countedRuns !== undefined;
      if (hasUses) {
        if (typeof e.uses !== 'number' || !Number.isFinite(e.uses)) return [];
        if (typeof e.countedRuns !== 'number' || !Number.isFinite(e.countedRuns)) {
          return [];
        }
      }
      methods.push({
        method: e.method,
        usd: e.usd,
        runs: e.runs,
        ...(hasUses
          ? { uses: e.uses as number, countedRuns: e.countedRuns as number }
          : {}),
      });
    }
    out.push({
      org: r.org as string,
      repo: r.repo as string,
      author: r.author as string,
      trigger: r.trigger as string,
      ...(r.pr !== undefined ? { pr: r.pr as string } : {}),
      ...(r.job !== undefined ? { job: r.job as string } : {}),
      savingsUsd: r.savingsUsd as number,
      savedRuns: r.savedRuns as number,
      ...(r.attrSavedRuns !== undefined
        ? { attrSavedRuns: r.attrSavedRuns as number }
        : {}),
      totalRuns: r.totalRuns as number,
      methods,
    });
  }
  return out;
}

export type WeeklyResponse = {
  isoYear: number;
  isoWeek: number;
  maxOffset: number;
  /** Present when the rows are scoped to one person; absent means all, collapsed. */
  actorFilter?: ActorFilter;
  /** Every actor known to the rollups, for the filter control. */
  actors: { authors: string[]; triggers: string[] };
  rows: WeeklyRow[];
  runCosts: RunCost[];
  runSavings: RunSaving[];
  lastReceived: Array<{ org: string; repo: string; receivedAt?: string }>;
};

/**
 * Validates the ENTIRE response, not just its rows.
 *
 * parseWeeklyRows covered one field of five. Each of the other four fails worse than the
 * panel's own error state if it arrives absent:
 *   maxOffset    -> effectiveMaxOffset falls back to offset+1 on every load, un-enforcing
 *                   the retention edge that the sticky-knownMax work exists to hold.
 *   lastReceived -> silences() runs in the component body, OUTSIDE the hook's try, so
 *                   `undefined.map` throws during render: a blank panel, not a warning.
 *   isoYear/Week -> the selector and summary title render "undefined - week undefined".
 *
 * Returns undefined when the envelope is unusable, so the caller can raise its own error
 * instead of rendering something confidently wrong.
 */
export function parseWeeklyResponse(
  input: unknown,
): { response: WeeklyResponse; invalidRows: number } | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const r = input as Record<string, unknown>;

  const ints = ['isoYear', 'isoWeek', 'maxOffset'] as const;
  if (!ints.every(f => typeof r[f] === 'number' && Number.isInteger(r[f]))) {
    return undefined;
  }
  if (!Array.isArray(r.lastReceived) || !Array.isArray(r.rows)) return undefined;

  const actors = r.actors as { authors?: unknown; triggers?: unknown } | undefined;
  if (
    !actors ||
    typeof actors !== 'object' ||
    !Array.isArray(actors.authors) ||
    !Array.isArray(actors.triggers) ||
    !actors.authors.every(a => typeof a === 'string') ||
    !actors.triggers.every(a => typeof a === 'string')
  ) {
    return undefined;
  }

  // Absent is valid (means unfiltered); malformed is not.
  let actorFilter: ActorFilter | undefined;
  if (r.actorFilter !== undefined) {
    const f = r.actorFilter as { field?: unknown; login?: unknown };
    if (
      !f ||
      typeof f !== 'object' ||
      (f.field !== 'author' && f.field !== 'trigger') ||
      typeof f.login !== 'string'
    ) {
      return undefined;
    }
    actorFilter = { field: f.field, login: f.login };
  }

  const lastReceived: WeeklyResponse['lastReceived'] = [];
  for (const entry of r.lastReceived) {
    if (!entry || typeof entry !== 'object') return undefined;
    const e = entry as Record<string, unknown>;
    if (typeof e.org !== 'string' || typeof e.repo !== 'string') return undefined;
    // receivedAt is legitimately absent for a repo whose raw rows were pruned.
    if (e.receivedAt !== undefined && typeof e.receivedAt !== 'string') return undefined;
    lastReceived.push({
      org: e.org,
      repo: e.repo,
      receivedAt: e.receivedAt as string | undefined,
    });
  }

  const { rows, invalid } = parseWeeklyRows(r.rows);
  return {
    response: {
      isoYear: r.isoYear as number,
      isoWeek: r.isoWeek as number,
      maxOffset: r.maxOffset as number,
      actorFilter,
      actors: {
        authors: actors.authors as string[],
        triggers: actors.triggers as string[],
      },
      rows,
      runCosts: parseRunCosts(r.runCosts),
      runSavings: parseRunSavings(r.runSavings),
      lastReceived,
    },
    invalidRows: invalid,
  };
}

/** The /daily envelope: same rows and grain, one calendar day, read from raw runs. */
export type DailyResponse = {
  /** YYYY-MM-DD in `tz`. Echoed by the server after validation. */
  day: string;
  /**
   * The IANA zone the boundaries were computed in. Optional because a server one
   * deploy behind does not echo it; absent means the pre-tz behavior, UTC.
   */
  tz?: string;
  /** Oldest raw row's day -- the floor for backwards navigation. Absent when no data. */
  oldestDay?: string;
  /** Newest raw row's day -- where the empty-today state offers to jump. */
  latestDay?: string;
  /** How long raw rows live; days older than this exist only as weekly rollups. */
  retentionWeeks: number;
  actorFilter?: ActorFilter;
  actors: { authors: string[]; triggers: string[] };
  rows: WeeklyRow[];
  runCosts: RunCost[];
  runSavings: RunSaving[];
  lastReceived: Array<{ org: string; repo: string; receivedAt?: string }>;
};

/** The /monthly envelope: the /daily shape with a month key -- same raw source. */
export type MonthlyResponse = Omit<DailyResponse, 'day'> & {
  /** YYYY-MM in `tz`. Echoed by the server after validation. */
  month: string;
};

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

/**
 * Validates the /daily envelope with the same posture as parseWeeklyResponse: every
 * field the panel dereferences outside the hook's try is checked here, because each
 * fails worse than an error panel if it arrives absent (day -> "undefined" in the
 * header and the export filename; lastReceived -> a render-time throw).
 */
export function parseDailyResponse(
  input: unknown,
): { response: DailyResponse; invalidRows: number } | undefined {
  const parsed = parseRawEnvelope(input, 'day', DAY_RE);
  if (!parsed) return undefined;
  return {
    response: { day: parsed.key, ...parsed.common },
    invalidRows: parsed.invalidRows,
  };
}

/** The /monthly twin -- one validator, two keys, so the two can never drift. */
export function parseMonthlyResponse(
  input: unknown,
): { response: MonthlyResponse; invalidRows: number } | undefined {
  const parsed = parseRawEnvelope(input, 'month', MONTH_RE);
  if (!parsed) return undefined;
  return {
    response: { month: parsed.key, ...parsed.common },
    invalidRows: parsed.invalidRows,
  };
}

function parseRawEnvelope(
  input: unknown,
  keyField: 'day' | 'month',
  keyRe: RegExp,
):
  | { key: string; common: Omit<DailyResponse, 'day'>; invalidRows: number }
  | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const r = input as Record<string, unknown>;

  const key = r[keyField];
  if (typeof key !== 'string' || !keyRe.test(key)) return undefined;
  if (r.tz !== undefined && typeof r.tz !== 'string') return undefined;
  if (r.oldestDay !== undefined) {
    if (typeof r.oldestDay !== 'string' || !DAY_RE.test(r.oldestDay)) return undefined;
  }
  if (r.latestDay !== undefined) {
    if (typeof r.latestDay !== 'string' || !DAY_RE.test(r.latestDay)) return undefined;
  }
  if (typeof r.retentionWeeks !== 'number' || !Number.isInteger(r.retentionWeeks)) {
    return undefined;
  }
  if (!Array.isArray(r.lastReceived) || !Array.isArray(r.rows)) return undefined;

  const actors = r.actors as { authors?: unknown; triggers?: unknown } | undefined;
  if (
    !actors ||
    typeof actors !== 'object' ||
    !Array.isArray(actors.authors) ||
    !Array.isArray(actors.triggers) ||
    !actors.authors.every(a => typeof a === 'string') ||
    !actors.triggers.every(a => typeof a === 'string')
  ) {
    return undefined;
  }

  let actorFilter: ActorFilter | undefined;
  if (r.actorFilter !== undefined) {
    const f = r.actorFilter as { field?: unknown; login?: unknown };
    if (
      !f ||
      typeof f !== 'object' ||
      (f.field !== 'author' && f.field !== 'trigger') ||
      typeof f.login !== 'string'
    ) {
      return undefined;
    }
    actorFilter = { field: f.field, login: f.login };
  }

  const lastReceived: DailyResponse['lastReceived'] = [];
  for (const entry of r.lastReceived) {
    if (!entry || typeof entry !== 'object') return undefined;
    const e = entry as Record<string, unknown>;
    if (typeof e.org !== 'string' || typeof e.repo !== 'string') return undefined;
    if (e.receivedAt !== undefined && typeof e.receivedAt !== 'string') return undefined;
    lastReceived.push({
      org: e.org,
      repo: e.repo,
      receivedAt: e.receivedAt as string | undefined,
    });
  }

  const { rows, invalid } = parseWeeklyRows(r.rows);
  return {
    key,
    common: {
      tz: r.tz as string | undefined,
      oldestDay: r.oldestDay as string | undefined,
      latestDay: r.latestDay as string | undefined,
      retentionWeeks: r.retentionWeeks,
      actorFilter,
      actors: {
        authors: actors.authors as string[],
        triggers: actors.triggers as string[],
      },
      rows,
      runCosts: parseRunCosts(r.runCosts),
      runSavings: parseRunSavings(r.runSavings),
      lastReceived,
    },
    invalidRows: invalid,
  };
}

/**
 * What the panel asks for: one period at any granularity. The union is
 * discriminated so a component can never read `maxOffset` off a day or `day` off
 * a week without the compiler objecting.
 */
export type Period =
  | { granularity: 'week'; offset: number }
  | { granularity: 'day'; day: string }
  | { granularity: 'month'; month: string };

export type PeriodResponse =
  | ({ granularity: 'week' } & WeeklyResponse)
  | ({ granularity: 'day' } & DailyResponse)
  | ({ granularity: 'month' } & MonthlyResponse);

/** Dollar formatting for reported run costs. Non-finite renders withheld, not $NaN. */
export function formatUsd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n > 0 && n < 0.005) return '< $0.01';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** One day of ingest activity: what arrived, regardless of the selected period. */
export type ActivityDay = { day: string; runs: number; tokens: number; repos: number };

/**
 * Validates the /activity envelope. Zero-days are DATA here (the chart's whole
 * point is that silence is visible), so a malformed entry rejects the envelope
 * rather than being dropped -- a chart quietly missing days would read as a
 * shorter healthy window.
 */
export function parseActivityResponse(
  input: unknown,
): { tz: string; days: ActivityDay[] } | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const r = input as Record<string, unknown>;
  if (typeof r.tz !== 'string' || !Array.isArray(r.days)) return undefined;
  const days: ActivityDay[] = [];
  for (const entry of r.days) {
    if (!entry || typeof entry !== 'object') return undefined;
    const e = entry as Record<string, unknown>;
    if (typeof e.day !== 'string' || !DAY_RE.test(e.day)) return undefined;
    const numbers = ['runs', 'tokens', 'repos'] as const;
    if (
      !numbers.every(
        f => typeof e[f] === 'number' && Number.isFinite(e[f] as number) && (e[f] as number) >= 0,
      )
    ) {
      return undefined;
    }
    days.push({
      day: e.day,
      runs: e.runs as number,
      tokens: e.tokens as number,
      repos: e.repos as number,
    });
  }
  return { tz: r.tz, days };
}

/** Today as a UTC calendar day. Weekly jump math only -- weeks are ISO/UTC. */
export const utcToday = (now: Date = new Date()): string =>
  now.toISOString().slice(0, 10);

/**
 * The viewer's IANA zone. Day and month views follow it -- their boundaries are
 * computed server-side from this name -- while weeks stay ISO weeks in UTC (the
 * persisted rollup grain, which relabeling would detach from the data).
 */
export const browserTz = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * The zone Anthropic reports in, and this panel's default.
 *
 * The Console's Cost and Usage pages bucket days in UTC, so a panel defaulting to
 * the viewer's zone disagreed with the invoice by up to a day at every boundary --
 * a CDT afternoon lands on the next UTC day, which reads as a mismatch rather than
 * as the offset it is. Defaulting here means the panel and the Console cut their
 * days at the same instant, and ISO weeks (already UTC) stop being the only
 * self-consistent granularity.
 */
export const ANTHROPIC_TZ = 'UTC';

/** Where the viewer's zone override lives. Panel-scoped; nothing else reads it. */
export const TZ_STORAGE_KEY = 'claude-metrics.tz';

/** True when `tz` is a zone Intl can actually resolve. */
export const isValidTz = (tz: string): boolean => {
  if (!tz) return false;
  try {
    // Called without `new` and asked for its options: constructing purely for the
    // throw is what `no-new` exists to catch, and resolvedOptions() makes the
    // resolution the point rather than a side effect.
    Intl.DateTimeFormat('en-US', { timeZone: tz }).resolvedOptions();
    return true;
  } catch {
    return false;
  }
};

/**
 * The zone to read in: a stored override when it is still resolvable, else UTC.
 *
 * Validated on the way OUT, not just in: a zone that was valid when stored can be
 * removed from the platform's tz database, and an unresolvable name would throw
 * inside every Intl call downstream rather than in one place here.
 */
export const loadTz = (store?: Pick<Storage, 'getItem'>): string => {
  try {
    const raw = (store ?? window.localStorage).getItem(TZ_STORAGE_KEY);
    return raw && isValidTz(raw) ? raw : ANTHROPIC_TZ;
  } catch {
    return ANTHROPIC_TZ;
  }
};

/** Persist a zone override, or clear it when it is the default. Never throws. */
export const saveTz = (
  tz: string,
  store?: Pick<Storage, 'setItem' | 'removeItem'>,
): void => {
  try {
    const s = store ?? window.localStorage;
    if (tz === ANTHROPIC_TZ) s.removeItem(TZ_STORAGE_KEY);
    else s.setItem(TZ_STORAGE_KEY, tz);
  } catch {
    /* a blocked or full store must not break the panel */
  }
};

/**
 * Zones to offer: UTC first, then the viewer's own, then the platform's full list.
 *
 * UTC and the browser zone are pinned to the top because they are the only two a
 * reader is likely to want, and deduped against the tail so neither appears twice
 * (the browser zone IS in supportedValuesOf, and UTC may be spelled there too).
 */
export const tzOptions = (browser: string = browserTz()): string[] => {
  let all: string[] = [];
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf;
    if (typeof supported === 'function') all = supported('timeZone');
  } catch {
    all = [];
  }
  const head = browser && browser !== ANTHROPIC_TZ ? [ANTHROPIC_TZ, browser] : [ANTHROPIC_TZ];
  return [...head, ...all.filter(z => !head.includes(z))];
};

/**
 * Re-anchor a day when the reading zone changes.
 *
 * Only "today" follows the zone. A reader parked on today expects to stay there,
 * but one parked on a past date picked that calendar day deliberately -- moving it
 * would silently change which data they are looking at. Late-evening CDT is already
 * the next UTC day, so without this a switch to UTC leaves the view on yesterday.
 */
export const reanchorDay = (
  day: string,
  fromTz: string,
  toTz: string,
  now: Date = new Date(),
): string => (day === todayIn(fromTz, now) ? todayIn(toTz, now) : day);

/** Month counterpart to reanchorDay -- same rule, month grain. */
export const reanchorMonth = (
  month: string,
  fromTz: string,
  toTz: string,
  now: Date = new Date(),
): string =>
  month === thisMonthIn(fromTz, now) ? thisMonthIn(toTz, now) : month;

/** Today as a calendar day in `tz` (en-CA renders YYYY-MM-DD natively). */
export const todayIn = (tz: string, now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

/** The current calendar month in `tz`, as YYYY-MM. */
export const thisMonthIn = (tz: string, now: Date = new Date()): string =>
  todayIn(tz, now).slice(0, 7);

/** The zone's short label ('CDT', 'GMT+2', 'UTC') for the period header. */
/**
 * A github.com URL built through the URL parser, every path segment
 * percent-encoded. The segments are our own pipeline's metadata (org, repo,
 * run id), but constructing beats interpolating: nothing upstream ever needs
 * to be proven harmless for the href to be.
 */
export function githubUrl(...segments: (string | number)[]): string {
  return new URL(
    segments.map(s => encodeURIComponent(String(s))).join('/'),
    'https://github.com',
  ).toString();
}

export const tzShortName = (tz: string, now: Date = new Date()): string => {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    })
      .formatToParts(now)
      .find(p => p.type === 'timeZoneName');
    return part?.value ?? tz;
  } catch {
    return tz;
  }
};

/** The day n days before/after a YYYY-MM-DD -- zone-agnostic calendar math. */
export function shiftDay(day: string, byDays: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + byDays)).toISOString().slice(0, 10);
}

/** This month as UTC YYYY-MM. Kept for tests; the panel uses thisMonthIn(tz). */
export const utcThisMonth = (now: Date = new Date()): string =>
  now.toISOString().slice(0, 7);

/** The month n months before/after a YYYY-MM -- zone-agnostic calendar math. */
export function shiftMonth(month: string, byMonths: number): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + byMonths, 1)).toISOString().slice(0, 7);
}

/** YYYY-MM of a YYYY-MM-DD -- pure truncation, whatever zone the day is in. */
export const monthOfDay = (day: string): string => day.slice(0, 7);

/**
 * Whole calendar weeks (Monday-anchored, UTC) between the week containing `fromDay`
 * and the week containing `toDay` -- the offset the /weekly API expects when jumping
 * to the week that holds a picked date. Never negative: a date in the current week
 * is offset 0. Plain Monday arithmetic, not ISO week NUMBERING -- offsets count
 * weeks back, so year-boundary week numbers never enter into it.
 */
export function weeksBack(fromDay: string, toDay: string): number {
  const mondayMs = (day: string) => {
    const [y, m, d] = day.split('-').map(Number);
    const t = Date.UTC(y, m - 1, d);
    const dow = new Date(t).getUTCDay() || 7; // Monday=1 .. Sunday=7
    return t - (dow - 1) * 86_400_000;
  };
  return Math.max(0, Math.round((mondayMs(fromDay) - mondayMs(toDay)) / (7 * 86_400_000)));
}

/**
 * Sums reported run costs under the current narrowing.
 *
 * Reported cost is a per-run figure: any narrowing by org/repo/author/trigger keeps
 * the sum exact (a run has exactly one of each), but a model narrowing SPLITS runs,
 * so the sum is withheld with the reason rather than shown wrong -- the same
 * contract runCount already follows in the breakdown.
 */
export function reportedCost(
  costs: readonly RunCost[],
  focus: readonly { dim: string; value: string }[],
): { usd: number; costedRuns: number; totalRuns: number } | { blockedBy: string } {
  // Only dims the cost rows themselves carry can filter them. Model always
  // withholds (it splits runs); pr and job filter exactly when the payload
  // carries them (all three read-time rollups do -- a run belongs to one PR and
  // has one job, so the sum stays exact) and withhold otherwise; team withholds
  // (never on the wire).
  //
  // job needs its own gate for the same reason pr does: withholding it would
  // print "a run's cost cannot be split there" under a Run type narrowing, which
  // is false and would contradict the dimension's whole premise.
  const capable = (field: 'pr' | 'job') =>
    costs.length > 0 && costs.every(c => c[field] !== undefined);
  const allowed = [
    'org',
    'repo',
    'author',
    'trigger',
    ...(capable('pr') ? ['pr'] : []),
    ...(capable('job') ? ['job'] : []),
  ];
  const blocked = focus.find(f => !allowed.includes(f.dim));
  if (blocked) return { blockedBy: blocked.dim };
  // Flat lookup rather than a ternary chain: pr and job are optional fields, the
  // rest are required, and the repo's lint bans nested ternaries.
  const valueAt = (c: RunCost, dim: string): string | undefined => {
    if (dim === 'pr') return c.pr;
    if (dim === 'job') return c.job;
    return c[dim as 'org' | 'repo' | 'author' | 'trigger'];
  };
  const filtered = focus.reduce<readonly RunCost[]>(
    (acc, f) => acc.filter(c => valueAt(c, f.dim) === f.value),
    costs,
  );
  let usd = 0;
  let costedRuns = 0;
  let totalRuns = 0;
  for (const c of filtered) {
    usd += c.costUsd;
    costedRuns += c.costedRuns;
    totalRuns += c.totalRuns;
  }
  return { usd, costedRuns, totalRuns };
}

/** The review job key and the zero-cost key its debounce-cancelled rounds emit under. */
export const REVIEW_JOB = 'claude-review';
export const SUPERSEDED_JOB = 'claude-review-superseded';

/**
 * The synchronize-debounce's yield under the current narrowing: review rounds
 * that were triggered but cancelled before any model spend (the reusable
 * workflow emits them as zero-cost runs under the `claude-review-superseded`
 * job key) as a share of all triggered review rounds, with the avoided spend
 * ESTIMATED at the narrowing's own mean completed-review cost.
 *
 * Exact-or-withheld like reportedCost: a dim the cost cells cannot filter
 * (model, team, or pr on an older payload) returns `blockedBy` so the tile
 * can say why. `job` is different: the split IS a job-key read, so every
 * cell must carry it — an older payload without it returns undefined before
 * any focus is judged, and the line simply never existed for that data.
 *
 * undefined also means NO superseded rounds under the narrowing — absence is
 * absence, not a zero claim: data that predates the debounce (or a fleet
 * whose pushes never went stale) shows nothing rather than "0%".
 *
 * estAvoidedUsd is an estimate, never metered dollars: superseded count x the
 * mean cost of the narrowing's COMPLETED review runs. When no completed run
 * carries a cost there is nothing to price against, and the share renders
 * unpriced — the same coverage-not-dollars posture the savings strip takes.
 */
export function supersededShare(
  costs: readonly RunCost[],
  focus: readonly { dim: string; value: string }[],
):
  | {
      superseded: number;
      triggeredRuns: number;
      share: number;
      meanReviewCostUsd?: number;
      estAvoidedUsd?: number;
    }
  | { blockedBy: string }
  | undefined {
  if (costs.length === 0 || costs.some(c => c.job === undefined)) return undefined;
  const capablePr = costs.every(c => c.pr !== undefined);
  const allowed = ['org', 'repo', 'author', 'trigger', 'job', ...(capablePr ? ['pr'] : [])];
  const blocked = focus.find(f => !allowed.includes(f.dim));
  if (blocked) return { blockedBy: blocked.dim };
  const valueAt = (c: RunCost, dim: string): string | undefined => {
    if (dim === 'pr') return c.pr;
    if (dim === 'job') return c.job;
    return c[dim as 'org' | 'repo' | 'author' | 'trigger'];
  };
  const filtered = focus.reduce<readonly RunCost[]>(
    (acc, f) => acc.filter(c => valueAt(c, f.dim) === f.value),
    costs,
  );
  let superseded = 0;
  let reviewRuns = 0;
  let reviewUsd = 0;
  let reviewCostedRuns = 0;
  for (const c of filtered) {
    if (c.job === SUPERSEDED_JOB) superseded += c.totalRuns;
    if (c.job === REVIEW_JOB) {
      reviewRuns += c.totalRuns;
      reviewUsd += c.costUsd;
      reviewCostedRuns += c.costedRuns;
    }
  }
  if (superseded === 0) return undefined;
  const triggeredRuns = superseded + reviewRuns;
  const priced = reviewCostedRuns > 0 && reviewUsd > 0;
  const meanReviewCostUsd = priced ? reviewUsd / reviewCostedRuns : undefined;
  return {
    superseded,
    triggeredRuns,
    share: superseded / triggeredRuns,
    ...(meanReviewCostUsd !== undefined
      ? { meanReviewCostUsd, estAvoidedUsd: superseded * meanReviewCostUsd }
      : {}),
  };
}

/**
 * Sums reported run savings under the current narrowing, with the consolidated
 * per-method breakdown for the totals strip — SPLIT by classification.
 *
 * `usd`/`methods` carry only intervention-attributable methods (counted uses:
 * an enforcement hook measured the method firing) plus coverage rows; ambient
 * pricing effects — priced-but-uncounted methods, i.e. `prompt-cache`, a
 * vendor discount no one chose per run — are returned separately under
 * `ambient` and NEVER summed into the savings figure. The reported cost column
 * already reflects cache pricing, so folding the discount into savings would
 * both double-represent it and drown the number this field exists to show.
 *
 * Caveat inherited from the structural rule: a future method that prices
 * itself WITHOUT counting uses lands on the ambient side — removed from this
 * headline — and needs revisiting here if it is ever meant to be savings.
 *
 * `savedRuns` is the attributable distinct-run count, taken from the
 * server-computed attrSavedRuns (method run counts overlap, so it cannot be
 * derived here); undefined when any cell predates the field — the "N of M
 * runs" claim is then withheld rather than approximated.
 *
 * Same exact-or-withheld contract as reportedCost -- savings are a per-run fact,
 * so a model narrowing (or any dim the cells don't carry) withholds rather than
 * approximates. totalRuns comes from the COST cells: savings cells exist only
 * where savings were reported, so their own denominators cannot see the runs
 * that reported none.
 */
export function reportedSavings(
  savings: readonly RunSaving[],
  costs: readonly RunCost[],
  focus: readonly { dim: string; value: string }[],
):
  | {
      usd: number;
      savedRuns: number | undefined;
      totalRuns: number;
      methods: {
        method: string;
        usd: number;
        runs: number;
        uses?: number;
        countedRuns?: number;
      }[];
      ambient: {
        usd: number;
        methods: { method: string; usd: number; runs: number }[];
      };
    }
  | { blockedBy: string } {
  const capable = (field: 'pr' | 'job') =>
    savings.length > 0 && savings.every(c => c[field] !== undefined);
  const allowed = [
    'org',
    'repo',
    'author',
    'trigger',
    ...(capable('pr') ? ['pr'] : []),
    ...(capable('job') ? ['job'] : []),
  ];
  const blocked = focus.find(f => !allowed.includes(f.dim));
  if (blocked) return { blockedBy: blocked.dim };
  const valueAt = (
    c: { org: string; repo: string; author: string; trigger: string; pr?: string; job?: string },
    dim: string,
  ): string | undefined => {
    if (dim === 'pr') return c.pr;
    if (dim === 'job') return c.job;
    return c[dim as 'org' | 'repo' | 'author' | 'trigger'];
  };
  const filtered = focus.reduce<readonly RunSaving[]>(
    (acc, f) => acc.filter(c => valueAt(c, f.dim) === f.value),
    savings,
  );
  let anySavedRuns = 0;
  let attrRuns: number | undefined = 0;
  const perMethod = new Map<
    string,
    { usd: number; runs: number; uses: number; countedRuns: number }
  >();
  for (const c of filtered) {
    anySavedRuns += c.savedRuns;
    // One pre-field cell poisons the distinct-run claim for the whole
    // narrowing: withheld, never approximated.
    if (attrRuns !== undefined && c.attrSavedRuns !== undefined) {
      attrRuns += c.attrSavedRuns;
    } else {
      attrRuns = undefined;
    }
    for (const m of c.methods) {
      const at = perMethod.get(m.method) ?? { usd: 0, runs: 0, uses: 0, countedRuns: 0 };
      at.usd += m.usd;
      at.runs += m.runs;
      if (m.uses !== undefined && m.countedRuns !== undefined) {
        at.uses += m.uses;
        at.countedRuns += m.countedRuns;
      }
      perMethod.set(m.method, at);
    }
  }
  // Classify the AGGREGATED entries (a method's counted-ness is a producer
  // property, stable across the runs of one narrowing).
  const entries = [...perMethod.entries()].map(([method, m]) => ({
    method,
    usd: m.usd,
    runs: m.runs,
    ...(m.countedRuns > 0 ? { uses: m.uses, countedRuns: m.countedRuns } : {}),
  }));
  const ambientEntries = entries.filter(
    m => m.usd > 0 && (m as { uses?: number }).uses === undefined,
  );
  const kept = entries.filter(
    m => !(m.usd > 0 && (m as { uses?: number }).uses === undefined),
  );
  const usd = kept.reduce((n, m) => n + m.usd, 0);
  // The denominator: distinct runs under the SAME narrowing, from the cost cells
  // (they cover every cell, savings or not). reportedCost already owns that math.
  const cost = reportedCost(costs, focus);
  // When the cost side is blocked but savings is not (the arrays validate
  // their dims independently), there is no honest all-runs denominator: fall
  // back to the attributable count itself so the hint degrades to "from all
  // N runs" instead of presenting the savings population as the total.
  const totalRuns =
    'blockedBy' in cost ? (attrRuns ?? anySavedRuns) : cost.totalRuns;
  return {
    usd,
    savedRuns: attrRuns,
    totalRuns,
    methods: kept.sort((a, b) => b.usd - a.usd || a.method.localeCompare(b.method)),
    ambient: {
      usd: ambientEntries.reduce((n, m) => n + m.usd, 0),
      methods: ambientEntries
        .map(({ method, usd: mUsd, runs }) => ({ method, usd: mUsd, runs }))
        .sort((a, b) => b.usd - a.usd || a.method.localeCompare(b.method)),
    },
  };
}

/**
 * Display glossary for the savings methods this UI knows about. The taxonomy
 * is OPEN by design — methods arrive as data from the reporting runs, never
 * as an enum — so this map is a display aid, not a registry: an unknown
 * method renders exactly like a known one, with the honest fallback below.
 * Keep entries in the reporting run's voice: what spend was avoided, and how.
 */
const SAVINGS_METHOD_GLOSSARY: Record<string, string> = {
  'prompt-cache':
    'A vendor pricing effect, shown as a dollar note beside the cache-read ' +
    'total — not savings this tooling created. Cache reads bill at a fraction of fresh input tokens ' +
    'and the run reports the difference, net of the write premium; the ' +
    'reported cost column already reflects this discount, so it is excluded ' +
    'from savings totals. Floored at zero per run (a cold run whose writes ' +
    'outweighed its reads records the loss in its run log, not here), so ' +
    'even the discount reads high across many runs.',
  'sqlite-context':
    'Estimated cost avoidance from querying the repo knowledge-graph SQLite ' +
    'sidecar instead of reading source files into context. Uses are ' +
    'measured; dollars are the reporting run’s declared estimate — each ' +
    'use priced as avoided turns at that run’s own average turn cost, ' +
    'never a price table.',
  'graph-context':
    'Estimated cost avoidance from consulting the knowledge graph before ' +
    'exploring: the dependency map replaces exploratory turns and discarded ' +
    'file reads. Uses are measured; dollars are the reporting run’s ' +
    'declared estimate — each use priced as avoided turns at that run’s ' +
    'own average turn cost, never a price table.',
  'blast-radius':
    'Coverage, not savings. Before the review starts, the changed files’ ' +
    'symbols and whatever depends on them elsewhere in the repo are read ' +
    'out of the knowledge graph and handed to the reviewer unasked, so it ' +
    'begins already knowing what the diff can reach. Deliberately unpriced: ' +
    'it fires on every review, including the majority that would never have ' +
    'run a query, so pricing each one as an avoided query would invent ' +
    'savings that never happened. The figure to read on this row is the run ' +
    'count — how many reviews it reached.',
};

/**
 * Does this method report COVERAGE rather than avoided spend?
 *
 * Structural, not a name list, because the method taxonomy is open (see the
 * glossary above): the reporting contract is that an entry shipping no use
 * count is deliberately unpriced, and an unpriced entry carries no dollars.
 * A priced method with nothing to report is OMITTED by its producer rather
 * than sent as zero -- absence is absence, not zero -- so `usd === 0` with no
 * count is the coverage shape, not a priced method that happened to save
 * nothing. A zero-dollar entry that DID count its uses is a pricing failure
 * rather than coverage, and is deliberately excluded here so it still reads
 * as a savings row worth explaining.
 */
export const isCoverageMethod = (m: { usd: number; uses?: number }): boolean =>
  m.usd === 0 && m.uses === undefined;

/** One sentence for a method's tooltip; honest about methods it has not met. */
export const describeSavingsMethod = (method: string): string =>
  SAVINGS_METHOD_GLOSSARY[method] ??
  `A savings method named by the reporting run ('${method}') — the method ` +
    'taxonomy is open, and the figure is what that run reported for it.';

export type Totals = {
  msgs: number;
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
};

/**
 * Sums the additive columns.
 *
 * `runCount` is deliberately NOT summed and not part of Totals. It is stored per
 * (org, repo, model), so a run that used two models contributes to two rows --
 * summing would double-count it, and the rollup does not retain the run ids needed
 * to do better. Run counts are therefore shown only on the per-model rows, where
 * they are exact.
 */
export function totals(rows: readonly WeeklyRow[]): Totals {
  const t: Totals = {
    msgs: 0,
    inputTokens: 0,
    outputTokens: 0,
    cacheWriteTokens: 0,
    cacheReadTokens: 0,
    totalTokens: 0,
  };
  for (const r of rows) {
    t.msgs += r.msgs;
    t.inputTokens += r.inputTokens;
    t.outputTokens += r.outputTokens;
    t.cacheWriteTokens += r.cacheWriteTokens;
    t.cacheReadTokens += r.cacheReadTokens;
  }
  t.totalTokens =
    t.inputTokens + t.outputTokens + t.cacheWriteTokens + t.cacheReadTokens;
  return t;
}

export const PROJECT_SLUG = 'github.com/project-slug';

export function ownersBySlug(
  entities: readonly {
    metadata?: { annotations?: Record<string, string> };
    spec?: { owner?: unknown };
  }[],
): Map<string, string> {
  const out = new Map<string, string>();
  for (const e of entities) {
    const slug = e.metadata?.annotations?.[PROJECT_SLUG];
    const owner = e.spec?.owner;
    if (typeof slug === 'string' && typeof owner === 'string' && owner) {
      out.set(slug.toLowerCase(), owner);
    }
  }
  return out;
}

/** Strips the `group:`/`user:` kind and any `default/` namespace for display. */
export function displayOwner(ownerRef: string | undefined): string | undefined {
  if (!ownerRef) return undefined;
  const withoutKind = ownerRef.includes(':')
    ? ownerRef.slice(ownerRef.indexOf(':') + 1)
    : ownerRef;
  return withoutKind.startsWith('default/') ? withoutKind.slice(8) : withoutKind;
}

export type Silence = {
  org: string;
  repo: string;
  /** Absent when the repo is known from rollups but has no un-pruned raw row. */
  receivedAt?: Date;
  /** Infinity when nothing recent is known -- sorts to the top, never reads as fresh. */
  ageDays: number;
  stale: boolean;
};

/**
 * Age of the last message per repo, flagging repos that have gone quiet.
 *
 * This exists because a broken emitter and a genuinely quiet week produce the same
 * empty panel. Anything past a week has missed at least one weekly window, so
 * STALE_AFTER_DAYS is 8 -- a day of slack so a repo that reviews on the same
 * weekday every week is not flagged for arriving a few hours late.
 */
export const STALE_AFTER_DAYS = 8;

export function silences(
  entries: readonly {
    org: string;
    repo: string;
    receivedAt?: string | Date | null;
  }[],
  now: Date,
): Silence[] {
  return entries
    .map(e => {
      // No timestamp means the repo is known from rollups but its raw rows are gone --
      // i.e. it has been quiet longer than the 12-week retention. That is the WORST
      // case, so it must not fall through to an age of 0 and read as reporting.
      if (e.receivedAt === undefined || e.receivedAt === null) {
        return { org: e.org, repo: e.repo, ageDays: Infinity, stale: true };
      }
      const receivedAt = new Date(e.receivedAt);
      if (Number.isNaN(receivedAt.getTime())) {
        return { org: e.org, repo: e.repo, ageDays: Infinity, stale: true };
      }
      const ageDays = (now.getTime() - receivedAt.getTime()) / 86_400_000;
      return {
        org: e.org,
        repo: e.repo,
        receivedAt,
        ageDays,
        stale: ageDays >= STALE_AFTER_DAYS,
      };
    })
    .sort(
      (a, b) =>
        // `||` rather than a bare subtraction: Infinity - Infinity is NaN, and two
        // never-reported repos is the day-one state, so a bare subtraction would leave
        // their relative order undefined -- and unstable between renders, since silences()
        // is recomputed with a fresh `now` each time. Fall through to a stable tiebreak.
        b.ageDays - a.ageDays ||
        `${a.org}/${a.repo}`.localeCompare(`${b.org}/${b.repo}`),
    );
}

/**
 * Headline for the "Reporting status" card. THREE states, not two.
 *
 * With no known repos at all -- fresh deploy, blank database, emitter never shipped,
 * queue misconfigured so nothing ever arrived -- there are no stale entries either, so a
 * two-state "any stale?" test falls through to the reassuring branch and renders a green
 * headline above a table saying nobody has ever reported. That is the inversion this card
 * exists to prevent, in the exact state the feature first ships in.
 */
export function reportingStatus(quiet: readonly Silence[]): string {
  if (quiet.length === 0) return 'No repo has ever reported';
  const stale = quiet.filter(s => s.stale).length;
  if (stale === 0) return 'All known repos reported within the last week';
  return stale === 1
    ? '1 repo has not reported in over a week'
    : `${stale} repos have not reported in over a week`;
}

/**
 * The offset bound to enforce right now.
 *
 * Sticky: the server's answer must survive the NEXT load. `data` is cleared on every
 * offset change, so a per-load `offset + 1` fallback un-enforces the edge during each
 * fetch -- at offset 11 mid-flight the "earlier" control stays live, one more click sends
 * offset=12, and the server's 400 surfaces as "Could not load usage metrics" for a request
 * the client already had the information to refuse.
 *
 * The bare fallback therefore applies only before the very first response, so the first
 * click is not swallowed.
 */
export function effectiveMaxOffset(
  serverMax: number | undefined,
  lastKnown: number | undefined,
  offset: number,
): number {
  return serverMax ?? lastKnown ?? offset + 1;
}

/** Compact token count: 1_234_567 -> "1.23M". */
export function formatTokens(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(n);
}
